// When a Supabase access token needs renewing.
//
// This was two slightly different inline comparisons — one in getSession, one
// in the background timer — and neither could be tested, because both were
// buried in a module that builds a live client at import time. The behaviour
// they encode is the whole reason a finished Executive generation was once
// thrown away, so it is worth being able to assert on.
//
// The margin is the important number. A token lives one hour; a generation
// streams for up to fifteen minutes and is only charged and stored when it
// finishes. Renewing at the last second means a stream that starts with four
// minutes of life left outlives its own credential and the write that follows
// fails. Renewing at five minutes, checked every minute, means no operation
// can span an expiry without the timer stepping in first.

export const REFRESH_MARGIN_S = 300; // renew once under five minutes of life remain
export const FRESHNESS_POLL_MS = 60_000; // check every minute, including mid-stream

export type SessionLike = { expires_at?: number; refresh_token?: string } | null;

export type RefreshDecision =
  | 'no-session' // nothing to renew
  | 'unknown-expiry' // pre-expires_at session: treat as suspect
  | 'refresh' // inside the margin, or already expired
  | 'fresh'; // good for now

/**
 * What should happen to `session` at `nowSeconds` (a Unix timestamp).
 *
 * `unknown-expiry` and `refresh` are deliberately distinct. A caller that
 * already needs a usable token should renew on either; the background timer
 * only acts on `refresh`, because a session with no `expires_at` gives it no
 * basis to decide and it would otherwise refresh on every single tick.
 */
export function refreshDecision(session: SessionLike, nowSeconds: number): RefreshDecision {
  if (!session) return 'no-session';
  if (!session.expires_at) return 'unknown-expiry';
  if (nowSeconds >= session.expires_at - REFRESH_MARGIN_S) return 'refresh';
  return 'fresh';
}

/**
 * Whether the background timer should renew now.
 *
 * Stricter than `refreshDecision`: without a refresh token there is nothing to
 * exchange, and firing anyway would clear the session out from under the user.
 */
export function shouldRefreshInBackground(session: SessionLike, nowSeconds: number): boolean {
  return refreshDecision(session, nowSeconds) === 'refresh' && !!session?.refresh_token;
}
